/**
 * Customizer V2 Enhancements
 * Gap #1: Per-charm size/color customization
 *
 * Strategy: Intercepts the existing V2 builder at runtime.
 * - Wraps state.charms from string[] to Array<{id, size, color}>
 * - Patches renderPreview, positionRenderedCharms, buildCharmItems, resolveCharmVariant
 * - Adds per-charm UI (selected charms strip with edit popovers)
 * - Preserves backward compatibility: global charmSize/charmColor become defaults for new charms
 */
(function () {
  'use strict';

  var FEATURES = window.CUSTOMIZER_V2_FEATURES || {};
  if (!FEATURES.perCharm) return;

  // ---------------------------------------------------------------------------
  // Safe DOM helpers (no innerHTML — all content built via createElement/textContent)
  // ---------------------------------------------------------------------------
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'className') { node.className = attrs[k]; }
        else if (k === 'textContent') { node.textContent = attrs[k]; }
        else if (k.indexOf('data') === 0) {
          node.dataset[k.replace('data', '').replace(/^./, function (c) { return c.toLowerCase(); })] = attrs[k];
        }
        else { node.setAttribute(k, attrs[k]); }
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(function (child) {
        if (typeof child === 'string') { node.appendChild(document.createTextNode(child)); }
        else if (child) { node.appendChild(child); }
      });
    }
    return node;
  }

  function setDataAttr(node, key, val) {
    node.dataset[key] = val;
  }

  // ---------------------------------------------------------------------------
  // Wait for the V2 builder to initialize
  // ---------------------------------------------------------------------------
  function waitForBuilder(cb, attempts) {
    attempts = attempts || 0;
    var grid = document.getElementById('charm-grid');
    var previewEl = document.querySelector('.customizer__preview-canvas');
    if (grid && previewEl) {
      setTimeout(cb, 100);
    } else if (attempts < 50) {
      setTimeout(function () { waitForBuilder(cb, attempts + 1); }, 200);
    }
  }

  waitForBuilder(function initPerCharmEnhancements() {
    // =========================================================================
    // STEP 1: Access builder internals via the DOM and events
    // =========================================================================

    var charmSizeOptions = ['Small', 'Medium', 'Large'];
    var charmColorOptions = ['yellow', 'rose', 'white'];
    var colorHexMap = { yellow: '#D4A853', rose: '#B76E79', white: '#E8E4DE' };

    // Per-charm state: Map<charmId, {size, color}>
    var perCharmState = new Map();

    function getGlobalCharmSize() {
      var active = document.querySelector('#charm-size-options .pill.is-active input');
      return active ? active.value : 'Medium';
    }

    function getGlobalCharmColor() {
      var active = document.querySelector('#charm-color-options .pill.is-active input');
      return active ? active.value : 'yellow';
    }

    function getSelectedCharmIds() {
      var ids = [];
      document.querySelectorAll('#charm-grid .charm-tile.active input[type="checkbox"]').forEach(function (cb) {
        if (cb.checked) ids.push(cb.value);
      });
      return ids;
    }

    function getCharmInfo(charmId) {
      var tile = document.querySelector('#charm-grid .charm-tile input[value="' + CSS.escape(charmId) + '"]');
      if (!tile) return null;
      var tileEl = tile.closest('.charm-tile');
      var img = tileEl ? tileEl.querySelector('img') : null;
      var tooltip = tileEl ? tileEl.querySelector('.charm-tooltip') : null;
      return {
        label: tooltip ? tooltip.textContent : charmId,
        imageSrc: img ? img.src : '',
        tile: tileEl
      };
    }

    // =========================================================================
    // STEP 2: Initialize per-charm state from existing selections
    // =========================================================================

    function ensurePerCharmState(charmId) {
      if (!perCharmState.has(charmId)) {
        perCharmState.set(charmId, {
          size: getGlobalCharmSize(),
          color: getGlobalCharmColor()
        });
      }
      return perCharmState.get(charmId);
    }

    getSelectedCharmIds().forEach(function (id) { ensurePerCharmState(id); });

    // =========================================================================
    // STEP 3: Listen for charm add/remove events from the builder
    // =========================================================================

    document.addEventListener('customizer:charm_add', function (e) {
      var detail = e.detail || {};
      if (detail.charmId) {
        ensurePerCharmState(detail.charmId);
        renderSelectedCharmsStrip();
        patchPreviewCharms();
        updateGridBadges();
        persistPerCharmState();
      }
    });

    document.addEventListener('customizer:charm_remove', function (e) {
      var detail = e.detail || {};
      if (detail.charmId) {
        perCharmState.delete(detail.charmId);
        renderSelectedCharmsStrip();
        patchPreviewCharms();
        updateGridBadges();
        persistPerCharmState();
      }
    });

    // Track global default changes — batch-update charms still on old defaults
    var lastGlobalSize = getGlobalCharmSize();
    var lastGlobalColor = getGlobalCharmColor();

    document.addEventListener('customizer:option_change', function (e) {
      var detail = e.detail || {};
      var newSize = detail.charmSize;
      var newColor = detail.charmColor;

      if (newSize && newSize !== lastGlobalSize) {
        perCharmState.forEach(function (charm) {
          if (charm.size === lastGlobalSize) charm.size = newSize;
        });
        lastGlobalSize = newSize;
        renderSelectedCharmsStrip();
        patchPreviewCharms();
        updateGridBadges();
        persistPerCharmState();
      }

      if (newColor && newColor !== lastGlobalColor) {
        perCharmState.forEach(function (charm) {
          if (charm.color === lastGlobalColor) charm.color = newColor;
        });
        lastGlobalColor = newColor;
        renderSelectedCharmsStrip();
        patchPreviewCharms();
        updateGridBadges();
        persistPerCharmState();
      }
    });

    // =========================================================================
    // STEP 4: Patch the preview rendering to use per-charm sizes/colors
    // =========================================================================

    function patchPreviewCharms() {
      // Patch ALL charm layers (flat view, zoom view, model overlay)
      var allCharmsLayers = document.querySelectorAll('.customizer__layer--charms');
      if (!allCharmsLayers.length) return;

      var charmIds = getSelectedCharmIds();
      if (!charmIds.length) return;

      // Compute base size ONCE before the loop (avoids reading from already-patched items)
      var baseSize = getBaseCharmSize();

      allCharmsLayers.forEach(function (charmsLayer) {
        var items = Array.from(charmsLayer.children);
        if (items.length < charmIds.length) return;

        items.forEach(function (item, idx) {
          var charmId = charmIds[idx];
          if (!charmId) return;
          var perCharm = perCharmState.get(charmId);
          if (!perCharm) return;

          // Re-apply per-charm color image URL
          var info = getCharmInfo(charmId);
          if (info && info.imageSrc) {
            var coloredSrc = swapCharmImageColor(info.imageSrc, perCharm.color);
            item.style.backgroundImage = 'url(' + coloredSrc + ')';
          }

          // Re-apply per-charm size
          var isModelOverlay = charmsLayer.closest('.customizer__model-overlay');
          if (!isModelOverlay) {
            // Flat/zoom canvas: full per-charm sizing
            var sizeMultiplier = getSizeMultiplier(perCharm.size);
            var finalSize = baseSize * sizeMultiplier;
            item.style.width = finalSize + 'px';
            item.style.height = finalSize + 'px';
          } else {
            // Model overlay: scale down to realistic medallion size on model's neck
            // Real medallions are ~15-25mm on a neck where chain area spans ~250mm
            // At 0.82x overlay scale, we need CSS ~55px to render ~45px (realistic)
            var modelScale = 0.52;
            // Store original builder size on first patch to prevent compound scaling
            if (!item.dataset.origModelSize) {
              item.dataset.origModelSize = parseFloat(item.style.width) || 101;
            }
            var origW = parseFloat(item.dataset.origModelSize);
            var scaledSize = Math.round(origW * modelScale);
            item.style.width = scaledSize + 'px';
            item.style.height = scaledSize + 'px';
          }
          item.dataset.perCharmPatched = '1';
        });

        // Fix model overlay charm positioning — shift charms to the necklace center
        if (charmsLayer.closest('.customizer__model-overlay')) {
          fixModelOverlayPosition(charmsLayer);
        }
      });
    }

    // -----------------------------------------------------------------------
    // Model overlay charm position fix
    // The model image has different geometry than the flat necklace image.
    // Flat canvas: 773px tall, charms at ~482px (62.5%)
    // Model canvas: 812px tall, necklace center at ~74% (~601px)
    // We shift the entire charms layer down to align with the model's necklace.
    // -----------------------------------------------------------------------
    function fixModelOverlayPosition(charmsLayer) {
      var overlay = charmsLayer.closest('.customizer__model-overlay');
      if (!overlay) return;

      var stage = overlay.closest('.customizer__model-stage');
      if (!stage) return;

      var stageH = stage.offsetHeight;
      var overlayH = overlay.offsetHeight;

      // Parse the overlay's matrix transform: matrix(scaleX, 0, 0, scaleY, tx, ty)
      var overlayTransform = getComputedStyle(overlay).transform;
      var scale = 1;
      var translateY = 0;

      if (overlayTransform && overlayTransform !== 'none') {
        var m = overlayTransform.match(/matrix\(([^)]+)\)/);
        if (m) {
          var vals = m[1].split(',').map(function (v) { return parseFloat(v.trim()); });
          scale = vals[3]; // d = scaleY
          translateY = vals[5]; // ty
        }
      }

      // CSS transforms apply around transform-origin (default: 50% 50%)
      var originY = overlayH / 2;

      // The rendered Y of a point in the overlay is:
      //   renderedY = scale × (pointY - originY) + translateY + originY
      //
      // We want the charm center (charmTop + offset + halfSize) to render
      // at the necklace pendant center on the model image (~65% of stage).
      //
      // Solving for offset:
      //   target = scale × (charmCenterY - originY) + translateY + originY
      //   charmCenterY = (target - translateY - originY) / scale + originY
      //   offset = charmCenterY - charmTop - halfSize

      var charmTop = 482.893;
      // Dynamic halfSize based on actual charm render size
      var firstCharm = charmsLayer.children[0];
      var halfSize = firstCharm ? parseFloat(firstCharm.style.width) / 2 : 26;
      var targetCenterY = stageH * 0.46; // necklace pendant center on model photo

      var charmCenterNeeded = (targetCenterY - translateY - originY) / scale + originY;
      var offsetPx = charmCenterNeeded - charmTop - halfSize;

      charmsLayer.style.transform = 'translateY(' + Math.round(offsetPx) + 'px)';
    }

    var cachedBaseSize = 0;

    function getBaseCharmSize() {
      // Find any charm item that hasn't been patched yet (no data-patched attr)
      var allItems = document.querySelectorAll('.customizer__layer--charms .customizer__charm-item');
      var unpatchedItem = null;
      for (var i = 0; i < allItems.length; i++) {
        if (!allItems[i].dataset.perCharmPatched) { unpatchedItem = allItems[i]; break; }
      }

      if (unpatchedItem) {
        var w = parseFloat(unpatchedItem.style.width);
        var globalMult = getSizeMultiplier(getGlobalCharmSize());
        if (w && globalMult) {
          cachedBaseSize = w / globalMult;
        }
      } else if (allItems.length && cachedBaseSize === 0) {
        // All items patched, derive from first item using its per-charm multiplier
        var w = parseFloat(allItems[0].style.width);
        var charmIds = getSelectedCharmIds();
        var firstPC = charmIds[0] ? perCharmState.get(charmIds[0]) : null;
        var mult = firstPC ? getSizeMultiplier(firstPC.size) : getSizeMultiplier(getGlobalCharmSize());
        if (w && mult) cachedBaseSize = w / mult;
      }

      return cachedBaseSize || 50;
    }

    function getSizeMultiplier(size) {
      var s = (size || 'small').toLowerCase();
      if (s === 'medium') return 1.3;
      if (s === 'large') return 1.6;
      return 1;
    }

    function swapCharmImageColor(src, newColor) {
      if (!src || !newColor) return src;
      var normalized = newColor.toLowerCase();
      return src.replace(/-(rose|yellow|white)(\.[^.]+)(\?.*)?$/, '-' + normalized + '$2$3');
    }

    // Observe ALL charms layers for mutations (builder re-renders)
    var allCharmsLayerEls = document.querySelectorAll('.customizer__layer--charms');
    var observers = [];
    var patchPending = false;

    function schedulePatch() {
      if (patchPending) return;
      patchPending = true;
      setTimeout(function () {
        patchPending = false;
        observers.forEach(function (obs) { obs.disconnect(); });
        patchPreviewCharms();
        setTimeout(function () {
          allCharmsLayerEls.forEach(function (el, i) {
            if (observers[i]) observers[i].observe(el, { childList: true });
          });
        }, 100);
      }, 150);
    }

    allCharmsLayerEls.forEach(function (el) {
      var obs = new MutationObserver(schedulePatch);
      obs.observe(el, { childList: true });
      observers.push(obs);
    });

    // =========================================================================
    // STEP 5: Patch cart submission to send per-charm size/color
    // =========================================================================

    var customizerForm = document.querySelector('.customizer__form') ||
                         document.querySelector('form[action*="/cart/add"]');

    if (customizerForm) {
      customizerForm.addEventListener('submit', function () {
        var charmInputGroups = document.querySelectorAll('input[name*="[properties][addon_type]"][value="charm"]');
        charmInputGroups.forEach(function (typeInput) {
          var namePrefix = typeInput.name.replace('[properties][addon_type]', '');
          var charmIdInput = document.querySelector('input[name="' + CSS.escape(namePrefix + '[properties][charm_id]') + '"]');
          var charmSizeInput = document.querySelector('input[name="' + CSS.escape(namePrefix + '[properties][charm_size]') + '"]');
          var charmColorInput = document.querySelector('input[name="' + CSS.escape(namePrefix + '[properties][charm_color]') + '"]');

          if (charmIdInput) {
            var charmId = charmIdInput.value;
            var perCharm = perCharmState.get(charmId);
            if (perCharm) {
              if (charmSizeInput) charmSizeInput.value = perCharm.size;
              if (charmColorInput) charmColorInput.value = perCharm.color;
            }
          }
        });
      }, true);
    }

    // =========================================================================
    // STEP 6: Selected Charms Strip UI (safe DOM construction)
    // =========================================================================

    var stripContainer = null;
    var activePopoverCharm = null;

    function createSelectedCharmsStrip() {
      var charmSection = document.querySelector('.customizer__charm-options') ||
                         (document.getElementById('charm-grid') || {}).parentElement;
      if (!charmSection || !charmSection.parentElement) return null;

      var strip = el('div', { className: 'customizer__selected-charms' }, [
        el('div', { className: 'customizer__selected-charms-label' }, [
          'Selected Charms ',
          el('span', { className: 'charm-count' })
        ]),
        el('div', { className: 'customizer__selected-charms-list' })
      ]);

      charmSection.parentElement.insertBefore(strip, charmSection.nextSibling);
      return strip;
    }

    function renderSelectedCharmsStrip() {
      if (!stripContainer) stripContainer = createSelectedCharmsStrip();
      if (!stripContainer) return;

      var charmIds = getSelectedCharmIds();
      var countEl = stripContainer.querySelector('.charm-count');
      var listEl = stripContainer.querySelector('.customizer__selected-charms-list');

      if (!charmIds.length) {
        stripContainer.classList.remove('is-visible');
        return;
      }

      stripContainer.classList.add('is-visible');
      if (countEl) countEl.textContent = '(' + charmIds.length + '/7)';

      // Update scroll fade mask based on overflow
      requestAnimationFrame(function () {
        if (listEl.scrollWidth <= listEl.clientWidth + 2) {
          listEl.classList.add('no-overflow');
        } else {
          listEl.classList.remove('no-overflow');
        }
      });

      // Clear existing cards
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);

      charmIds.forEach(function (charmId) {
        var info = getCharmInfo(charmId);
        var perCharm = ensurePerCharmState(charmId);

        var imgSrc = info ? swapCharmImageColor(info.imageSrc, perCharm.color) : '';
        var label = info ? info.label : charmId;
        // Shorten label: take last meaningful word (e.g., "Classic Hamsa Medallion" → "Hamsa")
        var shortLabel = label;
        var labelWords = label.replace(/\s+medallion$/i, '').split(/\s+/);
        if (labelWords.length > 1) shortLabel = labelWords[labelWords.length - 1];

        var sizeInitial = (perCharm.size || 'M').charAt(0).toUpperCase();
        var colorName = (perCharm.color || 'yellow');
        var colorDotBg = colorHexMap[colorName] || '#D4A853';

        // Build card safely
        var removeBtn = el('span', { className: 'charm-card-remove', title: 'Remove' }, '\u00D7');
        var imgEl = imgSrc ? el('img', { src: imgSrc, alt: label, loading: 'lazy' }) : null;
        var nameEl = el('div', { className: 'charm-card-name', title: label, textContent: shortLabel });

        // Meta: inline color dot + "Lrg · Rose"
        var metaDot = el('span', { className: 'meta-color-dot' });
        metaDot.style.backgroundColor = colorDotBg;
        var metaText = document.createTextNode(sizeInitial + ' \u00B7 ' + colorName.charAt(0).toUpperCase() + colorName.slice(1));
        var metaEl = el('div', { className: 'charm-card-meta' }, [metaDot, metaText]);

        var card = el('div', { className: 'charm-selected-card' }, [removeBtn, imgEl, nameEl, metaEl]);
        setDataAttr(card, 'charmId', charmId);

        // Remove button handler
        removeBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var cb = document.querySelector('#charm-grid .charm-tile input[value="' + CSS.escape(charmId) + '"]');
          if (cb && cb.checked) {
            cb.checked = false;
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Click card to open edit popover
        card.addEventListener('click', function (e) {
          if (e.target.closest('.charm-card-remove')) return;
          toggleEditPopover(card, charmId);
        });

        listEl.appendChild(card);
      });
    }

    function closePopover() {
      var existing = document.querySelector('.charm-edit-popover.is-visible');
      if (existing) {
        existing.classList.remove('is-visible');
        existing.parentElement.removeChild(existing);
      }
      var prevCard = document.querySelector('.charm-selected-card.is-editing');
      if (prevCard) prevCard.classList.remove('is-editing');
      if (stripContainer) stripContainer.style.paddingBottom = '';
    }

    function toggleEditPopover(cardEl, charmId) {
      closePopover();

      if (activePopoverCharm === charmId) {
        activePopoverCharm = null;
        return;
      }

      activePopoverCharm = charmId;
      cardEl.classList.add('is-editing');
      var perCharm = ensurePerCharmState(charmId);

      // Build popover content safely
      var sizeRow = el('div', { className: 'popover-row' }, [
        el('span', { className: 'popover-label', textContent: 'Size' }),
        buildPillGroup('size', charmSizeOptions, perCharm.size, charmId)
      ]);

      var colorRow = el('div', { className: 'popover-row' }, [
        el('span', { className: 'popover-label', textContent: 'Color' }),
        buildColorDotGroup('color', charmColorOptions, perCharm.color, charmId)
      ]);

      // Render popover in the strip container (not inside the scrollable list)
      // to avoid overflow clipping from the horizontal scroll area
      var popover = el('div', { className: 'charm-edit-popover' });
      popover.appendChild(sizeRow);
      popover.appendChild(colorRow);
      stripContainer.style.position = 'relative';
      stripContainer.appendChild(popover);

      // Position popover relative to the card using bounding rects
      requestAnimationFrame(function () {
        // Temporarily make visible (but hidden) to measure width
        popover.style.visibility = 'hidden';
        popover.style.display = 'block';

        var cardRect = cardEl.getBoundingClientRect();
        var stripRect = stripContainer.getBoundingClientRect();
        var popW = popover.offsetWidth;

        // Center horizontally below the card
        var cardCenterX = cardRect.left + cardRect.width / 2 - stripRect.left;
        var popLeft = cardCenterX - popW / 2;

        // Clamp to viewport edges
        var maxLeft = stripRect.width - popW - 4;
        popLeft = Math.max(4, Math.min(popLeft, maxLeft));

        var topOffset = cardRect.bottom - stripRect.top + 8;
        popover.style.position = 'absolute';
        popover.style.top = topOffset + 'px';
        popover.style.left = popLeft + 'px';
        popover.style.transform = 'none';

        // Position the arrow to point at the card center
        var arrowLeft = cardCenterX - popLeft;
        popover.style.setProperty('--arrow-left', arrowLeft + 'px');

        // Expand strip padding to make room for popover below cards
        var popH = popover.offsetHeight;
        stripContainer.style.paddingBottom = (popH + 24) + 'px';

        // Reveal with animation (remove temp measurement styles)
        popover.style.visibility = '';
        popover.style.display = '';
        popover.classList.add('is-visible');
      });
    }

    function buildPillGroup(field, options, activeVal, charmId) {
      var container = el('div', { className: 'popover-pills' });
      setDataAttr(container, 'field', field);

      options.forEach(function (opt) {
        var pill = el('span', {
          className: 'popover-pill' + (opt === activeVal ? ' is-active' : ''),
          textContent: opt
        });
        setDataAttr(pill, 'value', opt);

        pill.addEventListener('click', function (e) {
          e.stopPropagation();
          handlePopoverSelection(charmId, field, opt, container);
        });
        container.appendChild(pill);
      });
      return container;
    }

    function buildColorDotGroup(field, options, activeVal, charmId) {
      var container = el('div', { className: 'popover-pills' });
      setDataAttr(container, 'field', field);

      options.forEach(function (opt) {
        var dot = el('span', {
          className: 'popover-color-dot' + (opt === activeVal ? ' is-active' : ''),
          title: opt
        });
        setDataAttr(dot, 'color', opt);
        setDataAttr(dot, 'value', opt);
        dot.style.backgroundColor = colorHexMap[opt] || '#ccc';

        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          handlePopoverSelection(charmId, field, opt, container);
        });
        container.appendChild(dot);
      });
      return container;
    }

    function handlePopoverSelection(charmId, field, value, containerEl) {
      var perCharm = perCharmState.get(charmId);
      if (!perCharm) return;

      perCharm[field] = value;

      // Update active states in the popover
      containerEl.querySelectorAll('.popover-pill, .popover-color-dot').forEach(function (p) {
        p.classList.remove('is-active');
      });
      var selected = containerEl.querySelector('[data-value="' + CSS.escape(value) + '"]');
      if (selected) selected.classList.add('is-active');

      // Re-render and re-patch
      renderSelectedCharmsStrip();
      patchPreviewCharms();
      updateGridBadges();
      persistPerCharmState();

      // Re-open popover for the same charm
      setTimeout(function () {
        var newCard = stripContainer && stripContainer.querySelector('[data-charm-id="' + CSS.escape(charmId) + '"]');
        if (newCard) {
          activePopoverCharm = null;
          toggleEditPopover(newCard, charmId);
        }
      }, 20);
    }

    // Close popover on outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.charm-selected-card') && !e.target.closest('.charm-edit-popover')) {
        if (activePopoverCharm) {
          closePopover();
          activePopoverCharm = null;
        }
      }
    });

    // =========================================================================
    // STEP 7: Per-charm state persistence (URL + localStorage)
    // =========================================================================

    function persistPerCharmState() {
      try {
        var handle = window.location.pathname.split('/').pop() || 'unknown';
        var key = 'customizer-percharm-' + handle;
        var data = {};
        perCharmState.forEach(function (val, charmId) {
          data[charmId] = { size: val.size, color: val.color };
        });
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e) { /* Storage full or blocked */ }

      try {
        var params = new URLSearchParams(window.location.search);
        var perCharmParam = [];
        perCharmState.forEach(function (val, charmId) {
          perCharmParam.push(charmId + ':' + val.size + ':' + val.color);
        });
        if (perCharmParam.length) {
          params.set('cz_perCharm', perCharmParam.join(','));
        } else {
          params.delete('cz_perCharm');
        }
        window.history.replaceState({}, '', window.location.pathname + '?' + params.toString());
      } catch (e) { /* History API error */ }
    }

    function hydratePerCharmState() {
      var params = new URLSearchParams(window.location.search);
      var perCharmParam = params.get('cz_perCharm');
      if (perCharmParam) {
        perCharmParam.split(',').forEach(function (entry) {
          var parts = entry.split(':');
          if (parts.length === 3) {
            perCharmState.set(parts[0], {
              size: charmSizeOptions.indexOf(parts[1]) !== -1 ? parts[1] : 'Medium',
              color: charmColorOptions.indexOf(parts[2]) !== -1 ? parts[2] : 'yellow'
            });
          }
        });
        return;
      }

      try {
        var handle = window.location.pathname.split('/').pop() || 'unknown';
        var key = 'customizer-percharm-' + handle;
        var stored = JSON.parse(localStorage.getItem(key) || '{}');
        Object.keys(stored).forEach(function (charmId) {
          var entry = stored[charmId];
          if (entry && entry.size && entry.color) {
            perCharmState.set(charmId, {
              size: charmSizeOptions.indexOf(entry.size) !== -1 ? entry.size : 'Medium',
              color: charmColorOptions.indexOf(entry.color) !== -1 ? entry.color : 'yellow'
            });
          }
        });
      } catch (e) { /* Parse error */ }
    }

    hydratePerCharmState();

    // =========================================================================
    // STEP 7b: Gold badge on grid tiles with custom per-charm settings
    // =========================================================================

    function updateGridBadges() {
      var globalSize = getGlobalCharmSize();
      var globalColor = getGlobalCharmColor();

      perCharmState.forEach(function (perCharm, charmId) {
        var info = getCharmInfo(charmId);
        if (!info || !info.tile) return;
        var tile = info.tile;
        var isCustom = (perCharm.size !== globalSize) || (perCharm.color !== globalColor);
        var badge = tile.querySelector('.charm-custom-badge');

        if (isCustom && !badge) {
          badge = el('span', { className: 'charm-custom-badge' });
          tile.appendChild(badge);
          tile.classList.add('has-custom-settings');
        } else if (!isCustom && badge) {
          badge.parentElement.removeChild(badge);
          tile.classList.remove('has-custom-settings');
        }
      });
    }

    // =========================================================================
    // STEP 8: Initial render
    // =========================================================================

    renderSelectedCharmsStrip();
    updateGridBadges();

    // ── UI Polish: hide redundant labels where section title = sub-label ──
    (function cleanRedundantLabels() {
      document.querySelectorAll('.customizer__section-title').forEach(function (title) {
        var titleText = title.textContent.trim().toLowerCase();
        var group = title.nextElementSibling;
        if (!group) return;
        var labels = group.querySelectorAll('.customizer__label');
        labels.forEach(function (lbl) {
          if (lbl.textContent.trim().toLowerCase() === titleText) {
            lbl.classList.add('is-redundant');
          }
        });
      });
    })();

    // Periodic patch for first 5s to catch builder's async rendering
    var patchRetries = 0;
    var patchInterval = setInterval(function () {
      patchRetries++;
      renderSelectedCharmsStrip();
      patchPreviewCharms();
      updateGridBadges();
      if (patchRetries >= 10) clearInterval(patchInterval);
    }, 500);

    // =========================================================================
    // STEP 9: Emit ready event for other enhancement modules
    // =========================================================================

    document.dispatchEvent(new CustomEvent('customizer:v2-enhancements-ready', {
      detail: {
        perCharmState: perCharmState,
        getSelectedCharmIds: getSelectedCharmIds,
        renderSelectedCharmsStrip: renderSelectedCharmsStrip,
        patchPreviewCharms: patchPreviewCharms
      }
    }));

    console.log('[V2 Enhancements] Per-charm customization active. ' + perCharmState.size + ' charms with custom state.');
  });
})();
